import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Check, Crown, ShowerHead, Truck } from "lucide-react";

const PROVIDER_PLANS = [
  { id: "basico", name: "Básico", price: 59, features: ["Perfil no diretório", "Leads via WhatsApp", "Até 5 fotos", "1 bairro de atendimento"] },
  { id: "pro", name: "Pro", price: 129, features: ["Tudo do Básico", "Selo Verificado", "Estatísticas de leads", "Até 5 bairros", "Fotos ilimitadas"], highlight: true },
  { id: "premium", name: "Premium", price: 249, features: ["Tudo do Pro", "Destaque nas buscas", "Cupons cruzados de banho", "Cidade inteira", "Suporte prioritário"] },
];

export default function Plans() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [buying, setBuying] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", phone: "", neighborhood: "" });
  const [sendingLead, setSendingLead] = useState(false);

  useEffect(() => {
    if (user) api.post("/mp/sync").then(() => refresh()).catch(() => {});
  }, [user]);

  const checkout = async (payload, key) => {
    if (!user) return navigate("/login?next=/planos");
    setBuying(key);
    try {
      const r = await api.post("/payments/checkout", payload);
      if (r.data.init_point) { window.location.assign(r.data.init_point); return; }
      toast.success(`${r.data.order.item} ativado! (pagamento simulado)`, { duration: 5000 });
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no checkout");
    } finally {
      setBuying("");
    }
  };

  const submitLead = async () => {
    if (!lead.name.trim() || !lead.phone.trim()) return toast.error("Informe nome e telefone");
    setSendingLead(true);
    try {
      await api.post("/interest", { ...lead, topic: "banho_movel" });
      toast.success("Interesse registrado! Avisaremos você no lançamento do Banho Móvel.");
      setMobileOpen(false);
      setLead({ name: "", phone: "", neighborhood: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registrar");
    } finally {
      setSendingLead(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="plans-page">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <h1 className="text-3xl sm:text-4xl font-bold font-heading text-brand-graphite">Planos para todos</h1>
        <p className="text-muted-foreground mt-3">Tutores: PetHub Club com benefícios exclusivos. Prestadores: 7 dias de teste grátis, depois planos a partir de R$ 59/mês (anual com 2 meses grátis).</p>
      </div>

      <div className="bg-gradient-to-r from-brand-graphite to-[#3d3d3d] rounded-3xl p-8 md:p-10 text-white mb-14 grid md:grid-cols-2 gap-8 items-center" data-testid="club-plan-card">
        <div>
          <span className="inline-flex items-center gap-2 bg-brand-mustard text-brand-graphite text-xs font-bold px-3 py-1.5 rounded-full mb-4">
            <Crown className="w-3.5 h-3.5" /> PetHub Club — Early Bird
          </span>
          <h2 className="font-heading font-extrabold text-3xl">R$ 9,90<span className="text-lg font-normal text-white/70">/mês vitalício</span></h2>
          <p className="text-white/60 text-sm mt-1">Para os 100 primeiros assinantes. Depois, R$ 19,90/mês.</p>
          <ul className="mt-5 space-y-2.5 text-sm">
            {["Tag Club grátis (R$ 69 de valor)", "1 banho automático grátis por mês", "Cashback na vending machine", "Descontos em prestadores parceiros", "Prioridade no suporte"].map((f) => (
              <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-mint shrink-0" />{f}</li>
            ))}
          </ul>
        </div>
        <div className="text-center">
          {user?.club?.active ? (
            <div className="bg-white/10 rounded-2xl p-6" data-testid="club-active-badge">
              <Crown className="w-10 h-10 text-brand-mustard mx-auto mb-2" />
              <p className="font-heading font-bold text-xl">Você é membro do Club!</p>
              <p className="text-white/60 text-sm mt-1">{user.club.early_bird ? "Early Bird R$ 9,90 vitalício garantido." : "Obrigado por assinar."}</p>
            </div>
          ) : (
            <Button onClick={() => checkout({ kind: "club" }, "club")} disabled={buying === "club"}
              className="bg-brand-mustard hover:bg-brand-mustardDark text-brand-graphite rounded-full px-10 h-12 font-bold text-lg" data-testid="subscribe-club-btn">
              {buying === "club" ? "Processando..." : "Assinar o Club"}
            </Button>
          )}
        </div>
      </div>

      <h2 className="font-heading font-bold text-2xl text-brand-graphite text-center mb-2">Para Prestadores</h2>
      <p className="text-center text-muted-foreground text-sm mb-8">Comece com 7 dias grátis. Sem fidelidade, cancele quando quiser.</p>
      <div className="grid md:grid-cols-3 gap-6">
        {PROVIDER_PLANS.map((p) => (
          <div key={p.id} className={`bg-white rounded-3xl p-7 border-2 card-hover flex flex-col ${p.highlight ? "border-brand-coral shadow-lg" : "border-transparent"}`} data-testid={`plan-card-${p.id}`}>
            {p.highlight && <span className="self-start bg-brand-coral text-white text-xs font-bold px-3 py-1 rounded-full mb-3">Mais popular</span>}
            <p className="font-heading font-bold text-xl text-brand-graphite">{p.name}</p>
            <p className="font-heading font-extrabold text-4xl text-brand-graphite mt-2">R$ {p.price}<span className="text-base font-normal text-muted-foreground">/mês</span></p>
            <ul className="text-sm space-y-2 mt-5 mb-6 flex-1">
              {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-brand-graphite"><Check className="w-4 h-4 text-brand-mint mt-0.5 shrink-0" />{f}</li>)}
            </ul>
            <Button onClick={() => checkout({ kind: "plan", plan: p.id }, p.id)} disabled={buying === p.id}
              className={`rounded-full h-11 font-semibold ${p.highlight ? "bg-brand-coral hover:bg-brand-coralDark text-white" : "bg-brand-graphite hover:bg-black text-white"}`}
              data-testid={`subscribe-plan-${p.id}`}>
              {buying === p.id ? "Processando..." : "Assinar " + p.name}
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-14 bg-brand-mintLight rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="bath-booking-card">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-brand-mint rounded-2xl flex items-center justify-center shrink-0"><ShowerHead className="w-7 h-7 text-white" /></div>
          <div>
            <p className="font-heading font-bold text-xl text-brand-graphite">Banho avulso na máquina — R$ 69,90</p>
            <p className="text-sm text-muted-foreground">Reserve seu horário na estação PetMachine mais próxima. Membros Club têm 1 banho grátis/mês.</p>
          </div>
        </div>
        <div className="flex flex-col items-center gap-2 shrink-0">
          <Button onClick={() => checkout({ kind: "bath" }, "bath")} disabled={buying === "bath"}
            className="bg-brand-mint hover:bg-brand-mintDark text-white rounded-full px-8 h-11 font-semibold" data-testid="book-bath-btn">
            {buying === "bath" ? "Reservando..." : "Reservar banho"}
          </Button>
          <a href="https://petmachine.ind.br" target="_blank" rel="noreferrer" className="text-xs font-semibold text-brand-mintDark underline underline-offset-2" data-testid="partner-machine-cta">
            Seja parceiro: compre a sua máquina
          </a>
        </div>
      </div>

      <div className="mt-6 bg-brand-graphite rounded-3xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 text-white" data-testid="mobile-bath-card">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-brand-coral rounded-2xl flex items-center justify-center shrink-0"><Truck className="w-7 h-7 text-white" /></div>
          <div>
            <span className="inline-block bg-brand-mustard text-brand-graphite text-[10px] font-bold px-2.5 py-0.5 rounded-full mb-1.5">EM BREVE</span>
            <p className="font-heading font-bold text-xl">Banho Móvel PetMachine — vai até você</p>
            <p className="text-sm text-white/60">Nossa estação de banho sobre rodas atende no seu endereço, mediante agendamento. Cadastre seu interesse e seja avisado do lançamento.</p>
          </div>
        </div>
        <Button onClick={() => setMobileOpen(true)} className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full px-8 h-11 font-semibold shrink-0" data-testid="mobile-interest-btn">
          Quero ser avisado
        </Button>
      </div>

      <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Banho Móvel — lista de interesse</DialogTitle></DialogHeader>
          <div className="space-y-3" data-testid="mobile-interest-form">
            <p className="text-xs text-muted-foreground">Avisaremos quando o Banho Móvel começar a rodar em Joinville. Seu bairro nos ajuda a decidir a primeira rota!</p>
            <div><Label>Nome *</Label><Input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="mt-1" data-testid="mobile-lead-name" /></div>
            <div><Label>Telefone / WhatsApp *</Label><Input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="(47) 99999-9999" className="mt-1" data-testid="mobile-lead-phone" /></div>
            <div><Label>Bairro</Label><Input value={lead.neighborhood} onChange={(e) => setLead({ ...lead, neighborhood: e.target.value })} className="mt-1" data-testid="mobile-lead-neighborhood" /></div>
            <Button onClick={submitLead} disabled={sendingLead} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="mobile-lead-submit">
              {sendingLead ? "Enviando..." : "Cadastrar meu interesse"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
